import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    X, ChevronRight, ChevronLeft, MapPin, Building2,
    Clock, Users, Calendar, CheckCircle2, Sparkles,
    ShieldCheck, ArrowRight, Phone, Mail, User, FileText,
    MessageCircle, RefreshCw, Tag, AlertCircle,
    Check, HelpCircle, Info, AlertTriangle, Download,
    PlusCircle, Trash2
} from 'lucide-react';
import { generarPDFCotizaciones } from '../../utils/quotationPdf';
import { fechaCotizacion, finCotizacion } from '../../utils/quotationDates';
import logoComarca from '../../assets/images/logo/vertical.png';
import {
    fetchCatalogoCotizacion, crearCotizacionPublica, fetchDisponibilidad,
    CatalogoCotizacion, SucursalItem, CategoriaItem,
    ServicioItem, PrecioItem, EspacioItem, CotizacionResponse, ReservaItem
} from '../../services/landing/cotizacionService';
import { useAuth } from '../../context/AuthContext';

interface CotizacionModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface CotizacionPendiente {
    id: string;
    sucursalId: number;
    servicioId: number;
    precioId: number;
    espacioId: number | null;
    personas: number;
    cantidadPeriodos: number;
    diasHabiles?: boolean;
    fechaInicio: string;
    fechaFin: string;
    horaInicio: string;
    horaFin: string;
    observaciones: string;
    cuponCodigo: string;
    sucursalNombre: string;
    servicioNombre: string;
    espacioNombre: string;
    espacioDescripcion: string;
    planNombre: string;
    unidad: string;
    precioUnitario: number;
    precioEstimado: number;
    horas: number | null;
    dias: number | null;
    cantidadPersonasEspacio: number | null;
}

interface CotizacionFinalItem {
    id: string;
    pendiente: CotizacionPendiente;
    resultado: CotizacionResponse['cotizacion'];
}

// Brand color palette
const BRAND = {
    primary: '#61257b',
    primaryDark: '#451759',
    secondary: '#c1348b',
    secondaryLight: '#ffd97c',
    accentDark: '#ea8406',
    textDark: '#0f172a',
    textMuted: '#64748b',
    bgLight: '#f8fafc',
    border: '#e2e8f0',
};

const STEPS = [
    { title: 'Servicio', desc: 'Ubicación y servicio' },
    { title: 'Modalidad', desc: 'Tarifa y espacio' },
    { title: 'Horario', desc: 'Fecha y horas' },
    { title: 'Tus Datos', desc: 'Contacto rápido' },
    { title: 'Resumen', desc: 'Confirmación' },
];

const TIME_SLOTS = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00'
];

// ─── Helpers de Cookies (draft de cotización) ────────────────────────────
const DRAFT_COOKIE_NAME = 'cotizacion_draft_v1';
const DRAFT_COOKIE_DAYS = 1; // expira en 1 día

const setCookie = (name: string, value: string, days: number = 1) => {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

const getCookie = (name: string): string | null => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
};

const deleteCookie = (name: string) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

export const CotizacionModal: React.FC<CotizacionModalProps> = ({ isOpen, onClose }) => {
    const { user, isAuthenticated } = useAuth();
    const clavesEnvio = useRef(new Map<string, string>());
    // Current step: 0..4, and step 5 is confirmation view
    const [currentStep, setCurrentStep] = useState(0);

    // Catalog state
    const [catalogo, setCatalogo] = useState<CatalogoCotizacion | null>(null);
    const [loadingCatalogo, setLoadingCatalogo] = useState(false);
    const [catalogoError, setCatalogoError] = useState<string | null>(null);

    // Form Selections
    const [selectedSucursalId, setSelectedSucursalId] = useState<number | null>(null);
    const [selectedCategoriaId, setSelectedCategoriaId] = useState<number | null>(null);
    const [selectedServicioId, setSelectedServicioId] = useState<number | null>(null);
    const [selectedPrecioId, setSelectedPrecioId] = useState<number | null>(null);
    const [selectedEspacioId, setSelectedEspacioId] = useState<number | null>(null);
    const [personas, setPersonas] = useState<number | ''>(1);
    const [cantidadPeriodos, setCantidadPeriodos] = useState<number | ''>(1);

    // Scheduling
    const [fechaInicio, setFechaInicio] = useState<string>('');
    const [diasHabiles, setDiasHabiles] = useState(false);
    const [fechaFin, setFechaFin] = useState<string>('');
    const [horaInicio, setHoraInicio] = useState<string>('09:00');
    const [horaFin, setHoraFin] = useState<string>('11:00');

    // Disponibilidad y calendario
    const [reservas, setReservas] = useState<ReservaItem[]>([]);
    const [serverToday, setServerToday] = useState<string>('');
    const [loadingDisponibilidad, setLoadingDisponibilidad] = useState<boolean>(false);
    const [calYear, setCalYear] = useState<number>(() => new Date().getFullYear());
    const [calMonth, setCalMonth] = useState<number>(() => new Date().getMonth());

    // Client contact details (no login required)
    const [nombre, setNombre] = useState<string>('');
    const [apellido, setApellido] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [telefono, setTelefono] = useState<string>('');
    const [empresa, setEmpresa] = useState<string>('');
    const [ciNit, setCiNit] = useState<string>('');
    const [observaciones, setObservaciones] = useState<string>('');
    const [cuponCodigo, setCuponCodigo] = useState<string>('');

    // Submission state
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    // Multi-quotation state
    const [cotizacionesPendientes, setCotizacionesPendientes] = useState<CotizacionPendiente[]>([]);
    const [cotizacionesFinales, setCotizacionesFinales] = useState<CotizacionFinalItem[]>([]);
    // ID de la cotización pendiente que se está editando actualmente (null = nueva)
    const [editingCotizacionId, setEditingCotizacionId] = useState<string | null>(null);

    // Load catalog when modal opens
    useEffect(() => {
        if (!isOpen) return;
        if (!catalogo) {
            setLoadingCatalogo(true);
            setCatalogoError(null);
            fetchCatalogoCotizacion()
                .then(data => {
                    setCatalogo(data);
                    if (data.sucursales.length > 0) {
                        setSelectedSucursalId(data.sucursales[0].id);
                    }
                    if (data.categorias.length > 0) {
                        setSelectedCategoriaId(data.categorias[0].id);
                    }
                })
                .catch(err => setCatalogoError(err.message || 'Error al cargar servicios disponibles'))
                .finally(() => setLoadingCatalogo(false));
        }

        // Set default minimum date (today)
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const d = String(today.getDate()).padStart(2, '0');
        const todayFormatted = `${y}-${m}-${d}`;
        setFechaInicio(todayFormatted);
        setFechaFin(todayFormatted);
        setCalYear(y);
        setCalMonth(today.getMonth());
    }, [isOpen, catalogo]);

    // 🔹 Restaurar borrador de cookies al abrir el modal
    useEffect(() => {
        if (!isOpen) return;
        try {
            const raw = getCookie(DRAFT_COOKIE_NAME);
            if (!raw) return;
            const draft = JSON.parse(raw);

            if (Array.isArray(draft.cotizacionesPendientes)) {
                setCotizacionesPendientes(draft.cotizacionesPendientes);
            }
            if (draft.editingCotizacionId !== undefined) {
                setEditingCotizacionId(draft.editingCotizacionId);
            }
            // Datos de contacto (persistidos entre sesiones de cotización)
            if (draft.nombre) setNombre(draft.nombre);
            if (draft.apellido) setApellido(draft.apellido);
            if (draft.email) setEmail(draft.email);
            if (draft.telefono) setTelefono(draft.telefono);
            if (draft.empresa) setEmpresa(draft.empresa);
            if (draft.ciNit) setCiNit(draft.ciNit);
        } catch (e) {
            /* Browser diagnostics omitted. */
            deleteCookie(DRAFT_COOKIE_NAME);
        }
    }, [isOpen]);

    // 🔹 Si el usuario está registrado / autenticado en la plataforma, recuperar sus datos
    useEffect(() => {
        if (!isOpen) return;
        if (isAuthenticated && user) {
            if (user.first_name) setNombre(user.first_name);
            if (user.last_name) setApellido(user.last_name);
            if (user.email) setEmail(user.email);
            if (user.telefono) setTelefono(user.telefono);
            if (user.ci_nit) setCiNit(user.ci_nit);
            if (user.domiciliacion_empresa) setEmpresa(user.domiciliacion_empresa);
        }
    }, [isOpen, isAuthenticated, user]);

    // 🔹 Guardar borrador en cookies cuando cambien los datos relevantes
    useEffect(() => {
        if (!isOpen) return;
        // No guardar si aún no hay nada útil
        const hayPendientes = cotizacionesPendientes.length > 0;
        const hayContacto = !!(nombre || apellido || email || telefono);
        if (!hayPendientes && !hayContacto) return;

        try {
            const draft = {
                cotizacionesPendientes,
                editingCotizacionId,
                nombre, apellido, email, telefono, empresa, ciNit,
            };
            setCookie(DRAFT_COOKIE_NAME, JSON.stringify(draft), DRAFT_COOKIE_DAYS);
        } catch (e) {
            /* Browser diagnostics omitted. */
        }
    }, [
        isOpen,
        cotizacionesPendientes,
        editingCotizacionId,
        nombre, apellido, email, telefono, empresa, ciNit,
    ]);

    // Cada nueva cotización inicia un flujo independiente; conserva el contacto.
    const iniciarNuevaCotizacion = useCallback(() => {
        setCurrentStep(0);
        setCotizacionesPendientes([]);
        setCotizacionesFinales([]);
        setEditingCotizacionId(null);
        setSubmitError(null);
        setSelectedServicioId(null);
        setSelectedPrecioId(null);
        setSelectedEspacioId(null);
        setPersonas(1);
        setCantidadPeriodos(1);
        setObservaciones('');
        setCuponCodigo('');
        clavesEnvio.current.clear();
        deleteCookie(DRAFT_COOKIE_NAME);
    }, []);

    const estabaAbierto = useRef(isOpen);
    useEffect(() => {
        if (estabaAbierto.current && !isOpen) iniciarNuevaCotizacion();
        estabaAbierto.current = isOpen;
    }, [isOpen, iniciarNuevaCotizacion]);

    const handleClose = () => {
        iniciarNuevaCotizacion();
        onClose();
    };

    // Filter services according to selected branch and category
    const serviciosFiltrados = useMemo(() => {
        if (!catalogo) return [];
        return catalogo.servicios.filter(s => {
            const matchesCat = selectedCategoriaId ? s.categoria_id === selectedCategoriaId : true;
            const matchesSuc = selectedSucursalId ? (s.sucursales.length === 0 || s.sucursales.includes(selectedSucursalId)) : true;
            return matchesCat && matchesSuc;
        });
    }, [catalogo, selectedCategoriaId, selectedSucursalId]);

    // Auto-select first service if previous selection is not in filtered list
    useEffect(() => {
        if (serviciosFiltrados.length > 0) {
            if (!selectedServicioId || !serviciosFiltrados.some(s => s.id === selectedServicioId)) {
                setSelectedServicioId(serviciosFiltrados[0].id);
            }
        } else {
            setSelectedServicioId(null);
        }
    }, [serviciosFiltrados, selectedServicioId]);

    // Available prices for selected service
    const preciosDisponibles = useMemo(() => {
        if (!catalogo || !selectedServicioId) return [];
        return catalogo.precios.filter(p => p.servicio_id === selectedServicioId);
    }, [catalogo, selectedServicioId]);

    // Auto-select first price
    useEffect(() => {
        if (preciosDisponibles.length > 0) {
            if (!selectedPrecioId || !preciosDisponibles.some(p => p.id === selectedPrecioId)) {
                setSelectedPrecioId(preciosDisponibles[0].id);
            }
        } else {
            setSelectedPrecioId(null);
        }
    }, [preciosDisponibles, selectedPrecioId]);

    // Selected price object
    const currentPrecio = useMemo(() => {
        return preciosDisponibles.find(p => p.id === selectedPrecioId) || null;
    }, [preciosDisponibles, selectedPrecioId]);

    // Selected service object
    const currentServicio = useMemo(() => {
        if (!catalogo || !selectedServicioId) return null;
        return catalogo.servicios.find(s => s.id === selectedServicioId) || null;
    }, [catalogo, selectedServicioId]);

    // Available spaces for service & branch
    const espaciosDisponibles = useMemo(() => {
        if (!catalogo || !selectedServicioId) return [];
        return catalogo.espacios.filter(e => {
            const matchesServ = e.servicio_id === selectedServicioId;
            const matchesSuc = selectedSucursalId ? e.sucursal_id === selectedSucursalId : true;
            return matchesServ && matchesSuc;
        });
    }, [catalogo, selectedServicioId, selectedSucursalId]);

    // Auto-select first space if required
    useEffect(() => {
        if (currentServicio?.requiere_espacio && espaciosDisponibles.length > 0) {
            if (!selectedEspacioId || !espaciosDisponibles.some(e => e.id === selectedEspacioId)) {
                setSelectedEspacioId(espaciosDisponibles[0].id);
            }
        }
    }, [currentServicio, espaciosDisponibles, selectedEspacioId]);

    // Selected space object (disponible tempranamente para consultas de disponibilidad y aforo)
    const currentEspacio = useMemo(() => {
        return espaciosDisponibles.find(e => e.id === selectedEspacioId) || null;
    }, [espaciosDisponibles, selectedEspacioId]);

    // Fetch disponibilidad cuando cambie espacio físico, sucursal o servicio
    useEffect(() => {
        if (!isOpen) return;
        setLoadingDisponibilidad(true);
        const physicalEspacioId = currentEspacio?.espacio_id || null;
        fetchDisponibilidad({
            espacio_id: physicalEspacioId,
            espacio_servicio_id: selectedEspacioId,
            sucursal_id: selectedSucursalId,
            servicio_id: selectedServicioId,
        })
            .then(data => {
                setReservas(data.reservas || []);
                if (data.hoy) {
                    setServerToday(data.hoy);
                }
            })
            .catch(err => {
                /* Browser diagnostics omitted. */
            })
            .finally(() => {
                setLoadingDisponibilidad(false);
            });
    }, [isOpen, selectedEspacioId, currentEspacio?.espacio_id, selectedServicioId, selectedSucursalId]);

    // Today string
    const todayStr = useMemo(() => {
        if (serverToday) return serverToday;
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }, [serverToday]);

    // Map of reserved dates from backend Citas (filtrado exclusivamente por el espacio físico seleccionado)
    const fechasReservadasMap = useMemo(() => {
        const map: Record<string, ReservaItem[]> = {};
        const targetEspacioId = currentEspacio?.espacio_id;

        // Solo las reservas reales ocupan espacio; cotizar no bloquea horarios.
        for (const r of reservas) {
            if (!r.fecha_inicio) continue;
            if (currentServicio?.requiere_espacio && targetEspacioId) {
                if (r.espacio_id && r.espacio_id !== targetEspacioId) {
                    continue;
                }
            }
            const start = new Date(`${r.fecha_inicio}T00:00:00`);
            const endStr = r.fecha_fin || r.fecha_inicio;
            const end = new Date(`${endStr}T00:00:00`);
            const cur = new Date(start);
            while (cur <= end) {
                const y = cur.getFullYear();
                const m = String(cur.getMonth() + 1).padStart(2, '0');
                const d = String(cur.getDate()).padStart(2, '0');
                const dateStr = `${y}-${m}-${d}`;
                if (!map[dateStr]) map[dateStr] = [];
                map[dateStr].push(r);
                cur.setDate(cur.getDate() + 1);
            }
        }
        return map;
    }, [reservas, currentEspacio?.espacio_id, currentServicio?.requiere_espacio]);

    // Helper: Comprobar si un slot de hora de inicio (ej. "09:30") cae dentro de una reserva existente
    const isStartTimeOccupied = (slot: string, reservasDia: ReservaItem[]): boolean => {
        for (const r of reservasDia) {
            if (!r.hora_inicio || !r.hora_fin) {
                return true; // Todo el día ocupado
            }
            if (slot >= r.hora_inicio && slot < r.hora_fin) {
                return true;
            }
        }
        return false;
    };

    // Helper: Comprobar si un rango de horas [hIni, hFin) colisiona con alguna reserva del día
    const checkConflict = (hIni: string, hFin: string, reservasDia: ReservaItem[]): ReservaItem | null => {
        if (!hIni || !hFin) return null;
        for (const r of reservasDia) {
            if (!r.hora_inicio || !r.hora_fin) {
                return r; // Conflicto total día completo
            }
            if (hIni < r.hora_fin && hFin > r.hora_inicio) {
                return r;
            }
        }
        return null;
    };

    // Safe numbers
    const safePersonas = useMemo(() => {
        return (typeof personas === 'number' && personas >= 1) ? personas : 1;
    }, [personas]);

    const safeCantidadPeriodos = useMemo(() => {
        return (typeof cantidadPeriodos === 'number' && cantidadPeriodos >= 1) ? cantidadPeriodos : 1;
    }, [cantidadPeriodos]);

    // Time calculations
    const addHoursToTime = (timeStr: string, hoursToAdd: number): string => {
        if (!timeStr) return '11:00';
        const [hStr, mStr] = timeStr.split(':');
        let totalMinutes = parseInt(hStr, 10) * 60 + parseInt(mStr || '0', 10);
        totalMinutes += Math.round(hoursToAdd * 60);
        const maxMinutes = 23 * 60 + 59;
        if (totalMinutes > maxMinutes) totalMinutes = maxMinutes;
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };

    const diffInHours = (startStr: string, endStr: string): number => {
        if (!startStr || !endStr) return 1;
        const [h1, m1] = startStr.split(':').map(Number);
        const [h2, m2] = endStr.split(':').map(Number);
        const minutes1 = h1 * 60 + (m1 || 0);
        const minutes2 = h2 * 60 + (m2 || 0);
        const diffMinutes = minutes2 - minutes1;
        if (diffMinutes <= 0) return 1;
        return Math.max(1, Math.round(diffMinutes / 60));
    };

    const handleCantidadPeriodosChange = (val: number | '') => setCantidadPeriodos(val);

    const handleHoraInicioChange = (newHoraInicio: string) => {
        setHoraInicio(newHoraInicio);
        const periodos = safeCantidadPeriodos;
        const baseStep = (currentPrecio?.horas && currentPrecio.horas > 0) ? currentPrecio.horas : 1;
        const totalHours = currentPrecio?.dias ? baseStep : periodos * baseStep;
        setHoraFin(addHoursToTime(newHoraInicio, totalHours));
    };

    const handleHoraFinChange = (newHoraFin: string) => {
        setHoraFin(newHoraFin);
        if (currentPrecio?.unidad === 'hora' && !currentPrecio.dias && horaInicio && newHoraFin) {
            const baseStep = (currentPrecio?.horas && currentPrecio.horas > 0) ? currentPrecio.horas : 1;
            const totalHours = diffInHours(horaInicio, newHoraFin);
            const periodos = Math.max(1, Math.round(totalHours / baseStep));
            setCantidadPeriodos(periodos);
        }
    };

    const handleFechaInicioSelect = (dateStr: string) => {
        if (dateStr < todayStr) return; // Prevent selecting past dates
        setFechaInicio(dateStr);
        const reservasDelDia = fechasReservadasMap[dateStr] || [];

        // Si el horario de inicio actual o el rango coincide con una reserva de este día,
        // buscar automáticamente el primer slot disponible para comodidad del usuario
        if (currentPrecio?.unidad === 'hora' && reservasDelDia.length > 0) {
            const conflict = checkConflict(horaInicio, horaFin, reservasDelDia);
            if (conflict) {
                const firstFree = TIME_SLOTS.find(slot => !isStartTimeOccupied(slot, reservasDelDia));
                if (firstFree) {
                    setHoraInicio(firstFree);
                    const baseStep = (currentPrecio?.horas && currentPrecio.horas > 0) ? currentPrecio.horas : 1;
                    const totalHours = safeCantidadPeriodos * baseStep;
                    setHoraFin(addHoursToTime(firstFree, totalHours));
                }
            }
        }

        setFechaFin(finCotizacion(dateStr, currentPrecio?.dias || 1, currentPrecio?.dias || currentPrecio?.unidad === 'dia' ? safeCantidadPeriodos : 1, diasHabiles));
    };

    const formatDateSpanish = (dateStr: string): string => {
        if (!dateStr) return '';
        try {
            const [y, m, d] = dateStr.split('-').map(Number);
            const date = new Date(y, m - 1, d);
            const diasSemana = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
            const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
            return `${diasSemana[date.getDay()]}, ${d} de ${meses[m - 1]} de ${y}`;
        } catch {
            return dateStr;
        }
    };

    useEffect(() => {
        if (!currentPrecio || !fechaInicio) return;
        setFechaFin(finCotizacion(fechaInicio, currentPrecio.dias || 1, currentPrecio.dias || currentPrecio.unidad === 'dia' ? safeCantidadPeriodos : 1, diasHabiles));
        if (!currentPrecio.usa_temporizador && horaInicio && currentPrecio.horas) {
            const hours = currentPrecio.dias || currentPrecio.unidad === 'dia' ? currentPrecio.horas : currentPrecio.horas * safeCantidadPeriodos;
            setHoraFin(addHoursToTime(horaInicio, hours));
        }
    }, [currentPrecio, fechaInicio, safeCantidadPeriodos, horaInicio, diasHabiles]);

    // Estimated Price Calculation for summary
    const precioEstimado = useMemo(() => {
        if (!currentPrecio) return 0;
        return currentPrecio.precio_inicial * safeCantidadPeriodos;
    }, [currentPrecio, safeCantidadPeriodos]);

    // Selected or in-progress quotation object
    const cotizacionActualObj = useMemo<CotizacionPendiente | null>(() => {
        if (!selectedSucursalId || !selectedServicioId || !selectedPrecioId || !fechaInicio) return null;
        return {
            id: 'current',
            sucursalId: selectedSucursalId,
            servicioId: selectedServicioId,
            precioId: selectedPrecioId,
            espacioId: currentServicio?.requiere_espacio ? selectedEspacioId : null,
            personas: safePersonas,
            cantidadPeriodos: safeCantidadPeriodos,
            diasHabiles,
            fechaInicio,
            fechaFin: fechaFin || fechaInicio,
            horaInicio,
            horaFin,
            observaciones,
            cuponCodigo,
            sucursalNombre: catalogo?.sucursales.find(s => s.id === selectedSucursalId)?.nombre || '',
            servicioNombre: currentServicio?.nombre || '',
            espacioNombre: currentEspacio?.espacio_nombre || '',
            espacioDescripcion: currentEspacio?.espacio_descripcion || '',
            planNombre: currentPrecio?.plan_nombre || '',
            unidad: currentPrecio?.unidad || 'hora',
            precioUnitario: currentPrecio?.precio_inicial || 0,
            precioEstimado: precioEstimado,
            horas: currentPrecio?.horas || null,
            dias: currentPrecio?.dias || null,
            cantidadPersonasEspacio: currentEspacio?.cantidad_personas || null,
        };
    }, [
        selectedSucursalId, selectedServicioId, selectedPrecioId, selectedEspacioId,
        safePersonas, safeCantidadPeriodos, diasHabiles, fechaInicio, fechaFin, horaInicio, horaFin,
        observaciones, cuponCodigo, catalogo, currentServicio, currentEspacio, currentPrecio, precioEstimado
    ]);

    // All active quotations (previously saved + currently configured)
    const listaCotizacionesResumen = useMemo<CotizacionPendiente[]>(() => {
        // Excluir la que se está editando (su versión "viva" es cotizacionActualObj)
        const list = cotizacionesPendientes.filter(c => c.id !== editingCotizacionId);
        if (cotizacionActualObj) {
            // Preservar el id real para poder eliminarla correctamente
            list.push({
                ...cotizacionActualObj,
                id: editingCotizacionId || 'current',
            });
        }
        return list;
    }, [cotizacionesPendientes, cotizacionActualObj, editingCotizacionId]);

    // Grand total including all pending quotations + current
    const grandTotalEstimado = useMemo(() => {
        return listaCotizacionesResumen.reduce((sum, c) => sum + c.precioEstimado, 0);
    }, [listaCotizacionesResumen]);

    // Step navigation guards
    const canProceedToNext = () => {
        if (currentStep === 0) {
            return !!selectedSucursalId && !!selectedServicioId;
        }
        if (currentStep === 1) {
            if (!selectedPrecioId) return false;
            if (currentServicio?.requiere_espacio && !selectedEspacioId) return false;
            return true;
        }
        if (currentStep === 2) {
            if (!fechaInicio || fechaInicio < todayStr || !horaInicio) return false;
            if (currentPrecio?.unidad === 'hora') {
                const conf = checkConflict(horaInicio, horaFin, fechasReservadasMap[fechaInicio] || []);
                if (conf) return false;
            }
            return true;
        }
        if (currentStep === 3) {
            return !!nombre.trim() && !!apellido.trim() && !!email.trim() && !!telefono.trim();
        }
        return true;
    };

    // ✅ NUEVA FUNCIÓN AUXILIAR: guarda/actualiza la cotización actual en el array de pendientes
    const guardarCotizacionActualEnPendientes = () => {
        if (!selectedSucursalId || !selectedServicioId || !selectedPrecioId || !fechaInicio) return;

        const nueva: CotizacionPendiente = {
            id: editingCotizacionId || `tmp_${Date.now()}`,
            sucursalId: selectedSucursalId,
            servicioId: selectedServicioId,
            precioId: selectedPrecioId,
            espacioId: currentServicio?.requiere_espacio ? selectedEspacioId : null,
            personas: safePersonas,
            cantidadPeriodos: safeCantidadPeriodos,
            diasHabiles,
            fechaInicio,
            // Para unidad hora: fechaFin === fechaInicio (fix del bug del backend)
            fechaFin: fechaFin || fechaInicio,
            horaInicio,
            horaFin,
            observaciones,
            cuponCodigo,
            sucursalNombre: catalogo?.sucursales.find(s => s.id === selectedSucursalId)?.nombre || '',
            servicioNombre: currentServicio?.nombre || '',
            espacioNombre: currentEspacio?.espacio_nombre || '',
            espacioDescripcion: currentEspacio?.espacio_descripcion || '',
            planNombre: currentPrecio?.plan_nombre || '',
            unidad: currentPrecio?.unidad || 'hora',
            precioUnitario: currentPrecio?.precio_inicial || 0,
            precioEstimado: precioEstimado,
            horas: currentPrecio?.horas || null,
            dias: currentPrecio?.dias || null,
            cantidadPersonasEspacio: currentEspacio?.cantidad_personas || null,
        };

        if (editingCotizacionId) {
            // Actualizar la existente
            setCotizacionesPendientes(prev =>
                prev.map(c => c.id === editingCotizacionId ? nueva : c)
            );
        } else {
            // Añadir nueva
            setCotizacionesPendientes(prev => [...prev, nueva]);
        }
        setEditingCotizacionId(nueva.id);
    };

    // ✅ HANDLER REFACTORIZADO: usa la función de arriba en lugar de duplicar código
    const handleAgregarOtraCotizacion = () => {
        if (!selectedSucursalId || !selectedServicioId || !selectedPrecioId || !fechaInicio) return;

        // ✅ Guardar/actualizar cotización actual (una sola línea en lugar de 30)
        guardarCotizacionActualEnPendientes();

        // Reset para configurar una nueva cotización
        setEditingCotizacionId(null);
        setCurrentStep(0);
        setSelectedServicioId(null);
        setSelectedPrecioId(null);
        setSelectedEspacioId(null);
        setPersonas(1);
        setCantidadPeriodos(1);
        const now = new Date();
        const yy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const todayFmt = `${yy}-${mm}-${dd}`;
        setFechaInicio(todayFmt);
        setFechaFin(todayFmt);
        setHoraInicio('09:00');
        setHoraFin('11:00');
        setObservaciones('');
        setCuponCodigo('');
    };


    // Remove a pending quotation from the summary list
    const handleEliminarCotizacionPendiente = (id: string) => {
        if (id === editingCotizacionId) {
            setEditingCotizacionId(null);
        }

        if (id === 'current') {
            if (cotizacionesPendientes.length > 0) {
                const last = cotizacionesPendientes[cotizacionesPendientes.length - 1];
                setCotizacionesPendientes(prev => prev.slice(0, prev.length - 1));
                setSelectedSucursalId(last.sucursalId);
                setSelectedServicioId(last.servicioId);
                setSelectedPrecioId(last.precioId);
                setSelectedEspacioId(last.espacioId);
                setPersonas(last.personas);
                setCantidadPeriodos(last.cantidadPeriodos);
                setDiasHabiles(last.diasHabiles || false);
                setFechaInicio(last.fechaInicio);
                setFechaFin(last.fechaFin);
                setHoraInicio(last.horaInicio);
                setHoraFin(last.horaFin);
                setObservaciones(last.observaciones);
                setCuponCodigo(last.cuponCodigo);
            } else {
                setSelectedServicioId(null);
                setSelectedPrecioId(null);
                setSelectedEspacioId(null);
                setCurrentStep(0);
            }
        } else {
            setCotizacionesPendientes(prev => prev.filter(c => c.id !== id));
        }
    };

    // Remove a confirmed quotation from the final results list
    const handleEliminarCotizacion = (id: string) => {
        setCotizacionesFinales(prev => prev.filter(c => c.id !== id));
    };

    const handleNext = () => {
        if (personas === '' || personas < 1) setPersonas(1);
        if (cantidadPeriodos === '' || cantidadPeriodos < 1) setCantidadPeriodos(1);
        if (!canProceedToNext()) return;

        // Al salir del Paso 3, guardar/actualizar la cotización pendiente
        if (currentStep === 2) {
            guardarCotizacionActualEnPendientes();
        }

        setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    };

    const handleBack = () => {
        setCurrentStep(prev => Math.max(prev - 1, 0));
    };

    // Submit Quote Creation (supports multiple quotations)
    const handleGenerarCotizacion = async () => {
        if (listaCotizacionesResumen.length === 0) {
            setSubmitError('Por favor selecciona al menos un servicio para cotizar.');
            return;
        }
        if (!nombre.trim() || !email.trim() || !telefono.trim()) {
            setSubmitError('Por favor ingresa tu nombre, correo electrónico y teléfono.');
            return;
        }

        setSubmitting(true);
        setSubmitError(null);

        try {
            const resultados: CotizacionFinalItem[] = [];
            for (const cot of listaCotizacionesResumen) {
                const payload = {
                    sucursal_id: cot.sucursalId,
                    servicio_id: cot.servicioId,
                    precio_plan_servicio_id: cot.precioId,
                    espacio_servicio_id: cot.espacioId,
                    fecha_inicio: cot.fechaInicio,
                    // Garantía: servicios por hora nunca envían fechaFin distinta
                    fecha_fin: cot.fechaFin || cot.fechaInicio,
                    hora_inicio: cot.horaInicio,
                    hora_fin: catalogo?.precios.find(precio => precio.id === cot.precioId)?.usa_temporizador ? undefined : cot.horaFin,
                    cantidad_periodos: cot.cantidadPeriodos,
                    dias_habiles: cot.diasHabiles || false,
                    cupon_codigo: cot.cuponCodigo.trim() || undefined,
                    nombre,
                    apellido,
                    email,
                    telefono,
                    ci_nit: ciNit.trim() || undefined,
                    empresa: empresa.trim() || undefined,
                    observaciones: cot.observaciones.trim() || undefined,
                    personas: cot.personas,
                };
                const fingerprint = JSON.stringify({ id: cot.id, payload });
                let clave = clavesEnvio.current.get(fingerprint);
                if (!clave) {
                    const bytes = crypto.getRandomValues(new Uint8Array(16));
                    bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
                    const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
                    clave = `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
                    clavesEnvio.current.set(fingerprint, clave);
                }
                const response = await crearCotizacionPublica({ ...payload, clave_solicitud: clave });
                resultados.push({ id: cot.id, pendiente: cot, resultado: response.cotizacion });
                setCotizacionesFinales([...resultados]);
            }
            setCotizacionesFinales(resultados);
            setCurrentStep(5);
            deleteCookie(DRAFT_COOKIE_NAME);
        } catch (err: any) {
            setSubmitError(err.message || 'Error al procesar la cotización. Intenta nuevamente.');
        } finally {
            setSubmitting(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // PDF GENERATION
    // ─────────────────────────────────────────────────────────────────────────
    const generarPDFCotizacion = () => generarPDFCotizaciones(cotizacionesFinales.map(item => item.resultado));

    const handleDescargarPDF = async () => {
        try {
            const blob = await generarPDFCotizacion();
            if (blob) {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const codigos = cotizacionesFinales.map(c => c.resultado.codigo).join('_');
                a.download = `Cotizacion_Comarca_${codigos || 'reserva'}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(url), 2000);
            }
        } catch (err) {
            /* Browser diagnostics omitted. */
        }
    };

    const handleEnviarWhatsapp = (sucursal: string) => {
        const grupo = cotizacionesFinales.filter(item => item.resultado.sucursal === sucursal);
        if (grupo.length === 0) return;
        const primeraUrl = grupo[0]?.resultado?.whatsapp_url;
        if (!primeraUrl) return;

        if (grupo.length === 1) {
            window.open(primeraUrl, '_blank', 'noopener,noreferrer');
        } else {
            const codigos = grupo.map(c => c.resultado.codigo).join(', ');
            const total = grupo.reduce((s, c) => s + c.resultado.total, 0);
            const phoneMatch = primeraUrl.match(/wa\.me\/([0-9]+)/);
            if (!phoneMatch) return;
            const phone = phoneMatch[1];
            const msg = encodeURIComponent(`¡Hola Co Marca! Acabo de cotizar ${grupo.length} servicios en su sitio web:\n- Códigos de solicitud para registrar en POS: ${codigos}\n- Total General: Bs. ${total.toFixed(2)}\n\nQuisiera confirmar mi reserva.`);
            window.open(`https://wa.me/${phone}?text=${msg}`, '_blank', 'noopener,noreferrer');
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            animation: 'fadeIn 0.25s ease-out',
        }}>
            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(20px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
                @keyframes pulseGlow { 0%, 100% { box-shadow: 0 0 0 0 rgba(97,37,123,0.3); } 50% { box-shadow: 0 0 0 10px rgba(97,37,123,0); } }
                .cotiz-card { transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1); cursor: pointer; }
                .cotiz-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(97,37,123,0.12) !important; }
                .cotiz-input {
                    width: 100%;
                    padding: 12px 14px;
                    border-radius: 12px;
                    border: 1.5px solid #e2e8f0;
                    font-size: 0.93rem;
                    color: #0f172a;
                    background: #fff;
                    transition: border-color 0.2s, box-shadow 0.2s;
                    box-sizing: border-box;
                }
                .cotiz-input:focus {
                    outline: none;
                    border-color: #61257b;
                    box-shadow: 0 0 0 3px rgba(97,37,123,0.14);
                }
                @media (max-width: 768px) {
                    .step3-grid-container {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>

            <div style={{
                background: '#ffffff',
                width: '100%',
                maxWidth: '890px',
                maxHeight: '92vh',
                borderRadius: '24px',
                boxShadow: '0 25px 60px -15px rgba(15, 23, 42, 0.35)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                border: '1px solid rgba(255,255,255,0.8)',
            }}>
                {/* ═══ TOP HEADER ═══ */}
                <div style={{
                    padding: '20px 28px',
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'linear-gradient(135deg, #ffffff 0%, #faf7fc 100%)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '16px',
                            background: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            border: '1.5px solid #e2e8f0',
                            boxShadow: '0 6px 16px rgba(97,37,123,0.15)',
                            flexShrink: 0,
                        }}>
                            <img
                                src="/logo_comarca.png"
                                alt="Co Marca"
                                style={{
                                    width: '56px',
                                    height: '56px',
                                    objectFit: 'contain',
                                    display: 'block',
                                }}
                            />
                        </div>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                    fontSize: '0.72rem',
                                    fontWeight: '800',
                                    letterSpacing: '0.12em',
                                    textTransform: 'uppercase',
                                    color: BRAND.secondary,
                                }}>
                                    Co Marca Group
                                </span>
                                <span style={{
                                    fontSize: '0.68rem',
                                    fontWeight: '700',
                                    background: '#ecfdf5',
                                    color: '#059669',
                                    padding: '2px 8px',
                                    borderRadius: '6px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                }}>
                                    <ShieldCheck size={11} /> Sin Registro Previo
                                </span>
                            </div>
                            <h2 style={{
                                fontSize: '1.28rem',
                                fontWeight: '900',
                                color: BRAND.textDark,
                                letterSpacing: '-0.02em',
                                margin: 0,
                            }}>
                                {currentStep === 5 ? '¡Cotización guardada!' : 'Cotiza tu Servicio en Línea'}
                            </h2>
                        </div>
                    </div>

                    <button
                        onClick={handleClose}
                        style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            border: '1px solid #e2e8f0',
                            background: '#fff',
                            color: '#64748b',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor = '#f1f5f9';
                            e.currentTarget.style.color = '#0f172a';
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = '#fff';
                            e.currentTarget.style.color = '#64748b';
                        }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* ═══ STEP PROGRESS BAR (Only if not in confirmation) ═══ */}
                {currentStep < 5 && (
                    <div style={{ padding: '14px 28px 10px', background: '#fafafc', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            {STEPS.map((stepItem, idx) => {
                                const isDone = idx < currentStep;
                                const isActive = idx === currentStep;
                                const color = isActive ? BRAND.primary : isDone ? BRAND.secondary : '#94a3b8';

                                return (
                                    <div key={idx} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        flex: idx < STEPS.length - 1 ? 1 : 'none',
                                    }}>
                                        <div style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '50%',
                                            background: isActive ? BRAND.primary : isDone ? BRAND.secondary : '#f1f5f9',
                                            color: isActive || isDone ? '#fff' : '#64748b',
                                            fontSize: '0.75rem',
                                            fontWeight: '800',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: isActive ? '0 0 0 4px rgba(97,37,123,0.18)' : 'none',
                                            flexShrink: 0,
                                        }}>
                                            {isDone ? <Check size={14} strokeWidth={3} /> : idx + 1}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{
                                                fontSize: '0.78rem',
                                                fontWeight: '800',
                                                color: color,
                                                lineHeight: 1.1,
                                            }}>
                                                {stepItem.title}
                                            </span>
                                            <span style={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                                                {stepItem.desc}
                                            </span>
                                        </div>
                                        {idx < STEPS.length - 1 && (
                                            <div style={{
                                                flex: 1,
                                                height: '2px',
                                                background: isDone ? BRAND.secondary : '#e2e8f0',
                                                margin: '0 10px',
                                                borderRadius: '2px',
                                            }} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ═══ SCROLLABLE CONTENT BODY ═══ */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '24px 28px',
                    backgroundColor: '#ffffff',
                }}>
                    {loadingCatalogo && (
                        <div style={{ textAlign: 'center', padding: '60px 0', color: BRAND.textMuted }}>
                            <RefreshCw className="animate-spin" size={36} color={BRAND.primary} style={{ margin: '0 auto 12px' }} />
                            <p style={{ fontSize: '0.95rem', fontWeight: '600' }}>Cargando catálogo de espacios y servicios...</p>
                        </div>
                    )}

                    {catalogoError && (
                        <div style={{
                            padding: '16px',
                            borderRadius: '14px',
                            background: '#fef2f2',
                            border: '1.5px solid #fee2e2',
                            color: '#991b1b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '16px',
                        }}>
                            <AlertCircle size={20} />
                            <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>{catalogoError}</span>
                        </div>
                    )}

                    {!loadingCatalogo && catalogo && (
                        <>
                            {/* ═════════ PASO 1: UBICACIÓN Y SERVICIO ═════════ */}
                            {currentStep === 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                                    {/* Sucursal */}
                                    <div>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: '0.82rem',
                                            fontWeight: '800',
                                            letterSpacing: '0.04em',
                                            textTransform: 'uppercase',
                                            color: BRAND.textDark,
                                            marginBottom: '10px',
                                        }}>
                                            <MapPin size={16} color={BRAND.primary} /> 1. Elige la Sucursal
                                        </label>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                            gap: '12px',
                                        }}>
                                            {catalogo.sucursales.map(suc => {
                                                const selected = selectedSucursalId === suc.id;
                                                return (
                                                    <div
                                                        key={suc.id}
                                                        className="cotiz-card"
                                                        onClick={() => setSelectedSucursalId(suc.id)}
                                                        style={{
                                                            padding: '14px 18px',
                                                            borderRadius: '16px',
                                                            border: selected ? `2px solid ${BRAND.primary}` : '1.5px solid #e2e8f0',
                                                            background: selected ? 'linear-gradient(135deg, rgba(97,37,123,0.04) 0%, rgba(193,52,139,0.06) 100%)' : '#ffffff',
                                                            boxShadow: selected ? '0 6px 18px rgba(97,37,123,0.12)' : '0 2px 8px rgba(0,0,0,0.03)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '12px',
                                                        }}
                                                    >
                                                        <div style={{
                                                            width: '38px',
                                                            height: '38px',
                                                            borderRadius: '10px',
                                                            background: selected ? BRAND.primary : '#f1f5f9',
                                                            color: selected ? '#fff' : BRAND.primary,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                        }}>
                                                            <Building2 size={18} />
                                                        </div>
                                                        <div>
                                                            <div style={{ fontWeight: '800', fontSize: '0.93rem', color: BRAND.textDark }}>
                                                                {suc.nombre}
                                                            </div>
                                                            <div style={{ fontSize: '0.75rem', color: BRAND.textMuted, marginTop: '2px' }}>
                                                                {suc.direccion || 'Sede principal'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Categoría */}
                                    <div>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: '0.82rem',
                                            fontWeight: '800',
                                            letterSpacing: '0.04em',
                                            textTransform: 'uppercase',
                                            color: BRAND.textDark,
                                            marginBottom: '10px',
                                        }}>
                                            <Tag size={16} color={BRAND.secondary} /> 2. Categoría de Servicio
                                        </label>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            {catalogo.categorias.map(cat => {
                                                const selected = selectedCategoriaId === cat.id;
                                                return (
                                                    <button
                                                        key={cat.id}
                                                        type="button"
                                                        onClick={() => setSelectedCategoriaId(cat.id)}
                                                        style={{
                                                            padding: '8px 16px',
                                                            borderRadius: '100px',
                                                            border: selected ? 'none' : '1.5px solid #e2e8f0',
                                                            background: selected ? `linear-gradient(135deg, ${BRAND.secondary}, ${BRAND.primary})` : '#fff',
                                                            color: selected ? '#fff' : '#475569',
                                                            fontWeight: selected ? '800' : '600',
                                                            fontSize: '0.84rem',
                                                            cursor: 'pointer',
                                                            boxShadow: selected ? '0 4px 12px rgba(193,52,139,0.25)' : 'none',
                                                            transition: 'all 0.15s ease',
                                                        }}
                                                    >
                                                        {cat.nombre}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Servicios disponibles */}
                                    <div>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: '0.82rem',
                                            fontWeight: '800',
                                            letterSpacing: '0.04em',
                                            textTransform: 'uppercase',
                                            color: BRAND.textDark,
                                            marginBottom: '10px',
                                        }}>
                                            <Sparkles size={16} color={BRAND.accentDark} /> 3. Selecciona el Servicio
                                        </label>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                                            gap: '12px',
                                        }}>
                                            {serviciosFiltrados.map(serv => {
                                                const selected = selectedServicioId === serv.id;
                                                return (
                                                    <div
                                                        key={serv.id}
                                                        className="cotiz-card"
                                                        onClick={() => setSelectedServicioId(serv.id)}
                                                        style={{
                                                            padding: '16px',
                                                            borderRadius: '18px',
                                                            border: selected ? `2px solid ${BRAND.primary}` : '1.5px solid #e2e8f0',
                                                            background: selected ? 'linear-gradient(135deg, #ffffff 0%, rgba(97,37,123,0.05) 100%)' : '#ffffff',
                                                            boxShadow: selected ? '0 8px 24px rgba(97,37,123,0.14)' : '0 2px 8px rgba(0,0,0,0.03)',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'space-between',
                                                            minHeight: '110px',
                                                        }}
                                                    >
                                                        <div>
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                marginBottom: '6px',
                                                            }}>
                                                                <span style={{ fontWeight: '800', fontSize: '1rem', color: BRAND.textDark }}>
                                                                    {serv.nombre}
                                                                </span>
                                                                {serv.requiere_espacio && (
                                                                    <span style={{
                                                                        fontSize: '0.68rem',
                                                                        fontWeight: '700',
                                                                        background: '#f1f5f9',
                                                                        color: BRAND.primary,
                                                                        padding: '2px 8px',
                                                                        borderRadius: '6px',
                                                                    }}>
                                                                        Con Espacio
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p style={{
                                                                fontSize: '0.78rem',
                                                                color: BRAND.textMuted,
                                                                lineHeight: 1.45,
                                                                margin: 0,
                                                            }}>
                                                                {serv.descripcion || 'Servicio profesional optimizado para la colaboración y productividad.'}
                                                            </p>
                                                        </div>
                                                        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                                                            <span style={{
                                                                fontSize: '0.78rem',
                                                                fontWeight: '700',
                                                                color: selected ? BRAND.primary : '#94a3b8',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                            }}>
                                                                {selected ? '✓ Seleccionado' : 'Elegir servicio →'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ═════════ PASO 2: MODALIDAD Y ESPACIO ═════════ */}
                            {currentStep === 1 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                                    {/* Tarifas y Planes */}
                                    <div>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: '0.82rem',
                                            fontWeight: '800',
                                            letterSpacing: '0.04em',
                                            textTransform: 'uppercase',
                                            color: BRAND.textDark,
                                            marginBottom: '10px',
                                        }}>
                                            <Tag size={16} color={BRAND.primary} /> Elige la Modalidad / Tarifa
                                        </label>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                                            gap: '12px',
                                        }}>
                                            {preciosDisponibles.map(p => {
                                                const selected = selectedPrecioId === p.id;
                                                return (
                                                    <div
                                                        key={p.id}
                                                        className="cotiz-card"
                                                        onClick={() => setSelectedPrecioId(p.id)}
                                                        style={{
                                                            padding: '16px',
                                                            borderRadius: '16px',
                                                            border: selected ? `2px solid ${BRAND.secondary}` : '1.5px solid #e2e8f0',
                                                            background: selected ? 'linear-gradient(135deg, rgba(193,52,139,0.05) 0%, rgba(97,37,123,0.06) 100%)' : '#ffffff',
                                                            boxShadow: selected ? '0 8px 24px rgba(193,52,139,0.14)' : '0 2px 8px rgba(0,0,0,0.03)',
                                                        }}
                                                    >
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'flex-start',
                                                            marginBottom: '8px',
                                                        }}>
                                                            <div style={{ fontWeight: '800', fontSize: '0.98rem', color: BRAND.textDark }}>
                                                                {p.plan_nombre}
                                                            </div>
                                                            <span style={{
                                                                fontSize: '0.7rem',
                                                                fontWeight: '800',
                                                                textTransform: 'uppercase',
                                                                color: BRAND.secondary,
                                                                background: `${BRAND.secondary}15`,
                                                                padding: '2px 8px',
                                                                borderRadius: '6px',
                                                            }}>
                                                                {p.unidad === 'dia' ? 'Por Día' : 'Por Hora'}
                                                            </span>
                                                        </div>
                                                        <div style={{
                                                            fontSize: '1.4rem',
                                                            fontWeight: '900',
                                                            color: BRAND.primary,
                                                            lineHeight: 1.1,
                                                            marginBottom: '6px',
                                                        }}>
                                                            Bs. {p.precio_inicial.toFixed(2)}
                                                            <span style={{ fontSize: '0.75rem', fontWeight: '500', color: BRAND.textMuted }}>
                                                                {' '}/ {p.unidad === 'dia' ? (p.dias ? `${p.dias} día(s)` : 'día') : (p.horas ? `${p.horas} hora(s)` : 'hora')}
                                                            </span>
                                                        </div>
                                                        {p.precio_posterior && (
                                                            <div style={{ fontSize: '0.72rem', color: BRAND.textMuted }}>
                                                                Hora posterior: Bs. {p.precio_posterior.toFixed(2)}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Si requiere espacio físico */}
                                    {currentServicio?.requiere_espacio && (
                                        <div>
                                            <label style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontSize: '0.82rem',
                                                fontWeight: '800',
                                                letterSpacing: '0.04em',
                                                textTransform: 'uppercase',
                                                color: BRAND.textDark,
                                                marginBottom: '10px',
                                            }}>
                                                <Building2 size={16} color={BRAND.secondary} /> Selecciona el Espacio Físico
                                            </label>
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                                                gap: '12px',
                                            }}>
                                                {espaciosDisponibles.map(esp => {
                                                    const selected = selectedEspacioId === esp.id;
                                                    return (
                                                        <div
                                                            key={esp.id}
                                                            className="cotiz-card"
                                                            onClick={() => setSelectedEspacioId(esp.id)}
                                                            style={{
                                                                padding: '16px',
                                                                borderRadius: '16px',
                                                                border: selected ? `2px solid ${BRAND.primary}` : '1.5px solid #e2e8f0',
                                                                background: selected ? 'linear-gradient(135deg, rgba(97,37,123,0.05) 0%, #fff 100%)' : '#ffffff',
                                                                boxShadow: selected ? '0 8px 24px rgba(97,37,123,0.12)' : '0 2px 8px rgba(0,0,0,0.03)',
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                                <span style={{ fontWeight: '800', fontSize: '0.95rem', color: BRAND.textDark }}>
                                                                    {esp.espacio_nombre}
                                                                </span>
                                                                <span style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    fontSize: '0.72rem',
                                                                    fontWeight: '700',
                                                                    color: BRAND.accentDark,
                                                                    background: `${BRAND.accentDark}15`,
                                                                    padding: '2px 8px',
                                                                    borderRadius: '6px',
                                                                }}>
                                                                    <Users size={12} /> Aforo: {esp.cantidad_personas}
                                                                </span>
                                                            </div>
                                                            <p style={{ fontSize: '0.77rem', color: BRAND.textMuted, margin: 0 }}>
                                                                {esp.espacio_descripcion || 'Equipado con mobiliario ergonómico y conectividad de alta velocidad.'}
                                                            </p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Asistentes estimados (Permite borrar todo, auto-resetea a 1 al salir o avanzar) */}
                                    <div style={{
                                        background: '#f8fafc',
                                        padding: '18px 20px',
                                        borderRadius: '16px',
                                        border: '1px solid #f1f5f9',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: '16px',
                                    }}>
                                        <div style={{ maxWidth: '420px' }}>
                                            <label style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontSize: '0.85rem',
                                                fontWeight: '800',
                                                color: BRAND.textDark,
                                                marginBottom: '4px',
                                            }}>
                                                <Users size={18} color={BRAND.primary} /> Asistentes estimados
                                            </label>
                                            <p style={{ fontSize: '0.78rem', color: BRAND.textMuted, margin: 0 }}>
                                                {currentEspacio
                                                    ? `Aforo máximo recomendado para ${currentEspacio.espacio_nombre}: ${currentEspacio.cantidad_personas} personas.`
                                                    : 'Indica la cantidad aproximada de personas que harán uso del servicio.'}
                                            </p>
                                        </div>
                                        <div style={{ width: '130px' }}>
                                            <input
                                                type="number"
                                                min={1}
                                                max={currentEspacio?.cantidad_personas || 100}
                                                value={personas}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    if (val === '') {
                                                        setPersonas('');
                                                    } else {
                                                        const num = parseInt(val, 10);
                                                        if (!isNaN(num)) {
                                                            setPersonas(num);
                                                        }
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (personas === '' || personas < 1) {
                                                        setPersonas(1);
                                                    } else if (currentEspacio && personas > currentEspacio.cantidad_personas) {
                                                        setPersonas(currentEspacio.cantidad_personas);
                                                    }
                                                }}
                                                className="cotiz-input"
                                                style={{ fontWeight: '800', fontSize: '1.1rem', textAlign: 'center' }}
                                                placeholder="1"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ═════════ PASO 3: FECHA Y HORARIO ═════════ */}
                            {currentStep === 2 && (() => {
                                const monthNames = [
                                    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                                    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                                ];
                                const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
                                const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
                                const firstDayIndex = new Date(calYear, calMonth, 1).getDay();

                                const todayDate = new Date();
                                const canGoPrevMonth = calYear > todayDate.getFullYear() ||
                                    (calYear === todayDate.getFullYear() && calMonth > todayDate.getMonth());

                                const handlePrevMonth = () => {
                                    if (!canGoPrevMonth) return;
                                    setCalMonth(prev => {
                                        if (prev === 0) {
                                            setCalYear(y => y - 1);
                                            return 11;
                                        }
                                        return prev - 1;
                                    });
                                };

                                const handleNextMonth = () => {
                                    setCalMonth(prev => {
                                        if (prev === 11) {
                                            setCalYear(y => y + 1);
                                            return 0;
                                        }
                                        return prev + 1;
                                    });
                                };

                                const handleGoToday = () => {
                                    const now = new Date();
                                    setCalYear(now.getFullYear());
                                    setCalMonth(now.getMonth());
                                    if (todayStr) {
                                        handleFechaInicioSelect(todayStr);
                                    }
                                };

                                const reservasFechaSeleccionada = fechasReservadasMap[fechaInicio] || [];

                                return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        {/* Banner informativo */}
                                        <div style={{
                                            background: 'linear-gradient(135deg, rgba(97,37,123,0.06), rgba(193,52,139,0.05))',
                                            border: `1.5px solid ${BRAND.primary}20`,
                                            borderRadius: '16px',
                                            padding: '14px 18px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                        }}>
                                            <Calendar size={22} color={BRAND.primary} />
                                            <div>
                                                <div style={{ fontWeight: '800', fontSize: '0.92rem', color: BRAND.textDark }}>
                                                    Disponibilidad en Tiempo Real
                                                </div>
                                                <div style={{ fontSize: '0.76rem', color: BRAND.textMuted }}>
                                                    Las fechas reservadas u ocupadas se destacan en <strong style={{ color: '#dc2626' }}>rojo</strong>. No es posible seleccionar fechas pasadas.
                                                </div>
                                            </div>
                                        </div>

                                        {/* Calendario Interactivo Mensual */}
                                        <div style={{
                                            background: '#ffffff',
                                            border: '1.5px solid #e2e8f0',
                                            borderRadius: '18px',
                                            padding: '18px',
                                            boxShadow: '0 4px 16px rgba(0,0,0,0.03)',
                                        }}>
                                            {/* Header del Calendario con navegación de mes */}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginBottom: '16px',
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <span style={{ fontSize: '1.05rem', fontWeight: '900', color: BRAND.textDark }}>
                                                        {monthNames[calMonth]} {calYear}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={handleGoToday}
                                                        style={{
                                                            fontSize: '0.72rem',
                                                            fontWeight: '800',
                                                            color: BRAND.primary,
                                                            background: `${BRAND.primary}12`,
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            padding: '3px 8px',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        Ir a Hoy
                                                    </button>
                                                </div>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <button
                                                        type="button"
                                                        onClick={handlePrevMonth}
                                                        disabled={!canGoPrevMonth}
                                                        style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #e2e8f0',
                                                            background: canGoPrevMonth ? '#fff' : '#f8fafc',
                                                            color: canGoPrevMonth ? BRAND.textDark : '#cbd5e1',
                                                            cursor: canGoPrevMonth ? 'pointer' : 'not-allowed',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <ChevronLeft size={18} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleNextMonth}
                                                        style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #e2e8f0',
                                                            background: '#fff',
                                                            color: BRAND.textDark,
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <ChevronRight size={18} />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Días de la semana */}
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(7, 1fr)',
                                                gap: '6px',
                                                textAlign: 'center',
                                                marginBottom: '8px',
                                            }}>
                                                {dayNames.map(dayName => (
                                                    <span
                                                        key={dayName}
                                                        style={{
                                                            fontSize: '0.74rem',
                                                            fontWeight: '800',
                                                            color: '#64748b',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.04em',
                                                        }}
                                                    >
                                                        {dayName}
                                                    </span>
                                                ))}
                                            </div>

                                            {/* Celdas de los días */}
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(7, 1fr)',
                                                gap: '6px',
                                            }}>
                                                {/* Celdas vacías previas */}
                                                {Array.from({ length: firstDayIndex }).map((_, idx) => (
                                                    <div key={`empty-${idx}`} style={{ minHeight: '38px' }} />
                                                ))}

                                                {/* Días del mes actual */}
                                                {Array.from({ length: daysInMonth }).map((_, idx) => {
                                                    const dayNum = idx + 1;
                                                    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                                                    const isPast = dateStr < todayStr;
                                                    const isSelected = dateStr === fechaInicio;
                                                    const reservasEnDia = fechasReservadasMap[dateStr] || [];
                                                    const isReserved = reservasEnDia.length > 0;

                                                    return (
                                                        <button
                                                            key={`day-${dayNum}`}
                                                            type="button"
                                                            disabled={isPast}
                                                            onClick={() => handleFechaInicioSelect(dateStr)}
                                                            title={isPast
                                                                ? 'No disponible (fecha pasada)'
                                                                : isReserved
                                                                    ? `${reservasEnDia.length} reserva(s) registrada(s) en este espacio`
                                                                    : `Disponible (${dateStr})`}
                                                            style={{
                                                                minHeight: '38px',
                                                                borderRadius: '10px',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: isPast ? 'not-allowed' : 'pointer',
                                                                transition: 'all 0.15s ease',
                                                                position: 'relative',
                                                                padding: '4px 2px',
                                                                // Estilos según estado
                                                                ...(isSelected ? {
                                                                    background: BRAND.primary,
                                                                    color: '#ffffff',
                                                                    border: `2px solid ${BRAND.primary}`,
                                                                    fontWeight: '900',
                                                                    boxShadow: '0 4px 12px rgba(97,37,123,0.35)',
                                                                } : isReserved ? {
                                                                    background: '#fee2e2',
                                                                    color: '#dc2626',
                                                                    border: '1.5px solid #fca5a5',
                                                                    fontWeight: '800',
                                                                } : isPast ? {
                                                                    background: '#f8fafc',
                                                                    color: '#cbd5e1',
                                                                    border: '1px solid #f1f5f9',
                                                                    fontWeight: '500',
                                                                } : {
                                                                    background: '#ffffff',
                                                                    color: BRAND.textDark,
                                                                    border: '1px solid #e2e8f0',
                                                                    fontWeight: '700',
                                                                }),
                                                            }}
                                                        >
                                                            <span style={{ fontSize: '0.88rem', lineHeight: 1 }}>
                                                                {dayNum}
                                                            </span>
                                                            {/* Indicador de reserva en rojo */}
                                                            {isReserved && !isSelected && (
                                                                <span style={{
                                                                    width: '4px',
                                                                    height: '4px',
                                                                    borderRadius: '50%',
                                                                    background: '#dc2626',
                                                                    marginTop: '3px',
                                                                }} />
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            {/* Leyenda de colores del calendario */}
                                            <div style={{
                                                marginTop: '16px',
                                                paddingTop: '12px',
                                                borderTop: '1px solid #f1f5f9',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                flexWrap: 'wrap',
                                                gap: '8px',
                                                fontSize: '0.74rem',
                                                color: BRAND.textMuted,
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#ffffff', border: '1px solid #cbd5e1' }} />
                                                    <span>Disponible</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#fee2e2', border: '1.5px solid #fca5a5' }} />
                                                    <span style={{ color: '#dc2626', fontWeight: '700' }}>Con Reservas (Ocupado)</span>
                                                </div>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: BRAND.primary }} />
                                                    <span style={{ color: BRAND.primary, fontWeight: '700' }}>Seleccionada</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#f8fafc', border: '1px solid #e2e8f0' }} />
                                                    <span>Pasado (Bloqueado)</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Fecha Seleccionada y aviso si hay reservas */}
                                        <div style={{
                                            padding: '12px 16px',
                                            borderRadius: '12px',
                                            background: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            flexWrap: 'wrap',
                                            gap: '8px',
                                        }}>
                                            <div style={{ fontSize: '0.85rem', color: BRAND.textDark }}>
                                                <span style={{ fontWeight: '600', color: BRAND.textMuted }}>Fecha seleccionada: </span>
                                                <strong style={{ color: BRAND.primary }}>{formatDateSpanish(fechaInicio)}</strong>
                                            </div>
                                            {/* Selector nativo sincronizado con bloqueo de fechas pasadas */}
                                            <input
                                                type="date"
                                                min={todayStr}
                                                value={fechaInicio}
                                                onChange={e => handleFechaInicioSelect(e.target.value)}
                                                className="cotiz-input"
                                                style={{ padding: '6px 12px', fontSize: '0.82rem', width: 'auto' }}
                                            />
                                        </div>

                                        {/* Aviso si la fecha elegida tiene reservas registradas */}
                                        {reservasFechaSeleccionada.length > 0 && (
                                            <div style={{
                                                padding: '12px 16px',
                                                borderRadius: '12px',
                                                background: '#fef2f2',
                                                border: '1.5px solid #fecaca',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '10px',
                                                color: '#991b1b',
                                                fontSize: '0.82rem',
                                            }}>
                                                <AlertCircle size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                                                <div>
                                                    <strong>Esta fecha tiene {reservasFechaSeleccionada.length} reserva(s) registrada(s):</strong>
                                                    <div style={{ marginTop: '3px', fontWeight: '500' }}>
                                                        {reservasFechaSeleccionada.map(r => r.hora_inicio ? `${r.hora_inicio} a ${r.hora_fin}` : 'Día completo').join(', ')}
                                                    </div>
                                                    <span style={{ fontSize: '0.75rem', color: '#b91c1c', marginTop: '2px', display: 'block' }}>
                                                        Asegúrate de definir un horario que no coincida con los horarios ya ocupados.
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Configuración de Cantidad de Horas y Horarios con recálculo bidireccional */}
                                        <div style={{
                                            background: '#f8fafc',
                                            padding: '18px',
                                            borderRadius: '16px',
                                            border: '1px solid #e2e8f0',
                                        }}>
                                            <label style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontSize: '0.82rem',
                                                fontWeight: '800',
                                                letterSpacing: '0.04em',
                                                textTransform: 'uppercase',
                                                color: BRAND.textDark,
                                                marginBottom: '14px',
                                            }}>
                                                <Clock size={16} color={BRAND.accentDark} />
                                                {currentPrecio?.unidad === 'dia' ? 'Duración y Fechas del Servicio' : 'Cantidad de Horas y Horario'}
                                            </label>

                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: currentPrecio?.unidad === 'dia' ? 'repeat(auto-fit, minmax(200px, 1fr))' : 'repeat(auto-fit, minmax(180px, 1fr))',
                                                gap: '14px',
                                            }}>
                                                {/* Cantidad de Horas o Días (Permite borrar y escribir limpiamente) */}
                                                <div>
                                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '800', color: BRAND.textDark, marginBottom: '6px' }}>
                                                        {(currentPrecio?.dias && currentPrecio.dias > 1) || (currentPrecio?.unidad === 'hora' && (currentPrecio.dias || (currentPrecio.horas || 1) > 1)) ? 'Cantidad de períodos:' : currentPrecio?.unidad === 'dia' ? 'Cantidad de Días:' : 'Cantidad de Horas:'}
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        max={currentPrecio?.unidad === 'dia' ? 365 : 24}
                                                        value={cantidadPeriodos}
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            if (val === '') {
                                                                setCantidadPeriodos('');
                                                            } else {
                                                                const num = parseInt(val, 10);
                                                                if (!isNaN(num)) {
                                                                    handleCantidadPeriodosChange(num);
                                                                }
                                                            }
                                                        }}
                                                        onBlur={() => {
                                                            if (cantidadPeriodos === '' || cantidadPeriodos < 1) {
                                                                handleCantidadPeriodosChange(1);
                                                            }
                                                        }}
                                                        className="cotiz-input"
                                                        style={{ fontWeight: '800' }}
                                                        placeholder="1"
                                                    />
                                                </div>

                                                {/* Hora de Inicio */}
                                                <div>
                                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '800', color: BRAND.textDark, marginBottom: '6px' }}>
                                                        Hora de Inicio:
                                                    </label>
                                                    <input
                                                        type="time"
                                                        value={horaInicio}
                                                        onChange={e => handleHoraInicioChange(e.target.value)}
                                                        className="cotiz-input"
                                                    />
                                                </div>

                                                {/* Hora Estimada de Fin (al cambiarla, Cantidad de Horas se actualiza automáticamente) */}
                                                <div>
                                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '800', color: BRAND.textDark, marginBottom: '6px' }}>
                                                        Hora Estimada de Fin:
                                                    </label>
                                                    <input
                                                        type="time"
                                                        value={horaFin}
                                                        onChange={e => handleHoraFinChange(e.target.value)}
                                                        className="cotiz-input"
                                                    />
                                                </div>

                                                {/* Fecha de Fin para planes por día */}
                                                {(currentPrecio?.unidad === 'dia' || Boolean(currentPrecio?.dias)) && (
                                                    <div>
                                                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '800', color: BRAND.textDark, marginBottom: '6px' }}>
                                                            Fecha de Fin:
                                                        </label>
                                                        <input
                                                            type="date"
                                                            value={fechaFin}
                                                            min={fechaInicio}
                                                            readOnly
                                                            className="cotiz-input"
                                                        />
                                                        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}><input type="checkbox" checked={diasHabiles} onChange={e => setDiasHabiles(e.target.checked)} />Solo días hábiles (lunes a viernes)</label>
                                                    </div>
                                                )}
                                            </div>

                                            <div style={{ marginTop: '10px', fontSize: '0.74rem', color: BRAND.textMuted, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <Info size={14} color={BRAND.primary} />
                                                <span>Al modificar la hora fin o la hora de inicio, la <strong>Cantidad de Horas</strong> se sincroniza automáticamente.</span>
                                            </div>
                                        </div>

                                        {/* Botón para agregar otra cotización */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            <button
                                                type="button"
                                                onClick={handleAgregarOtraCotizacion}
                                                disabled={!canProceedToNext()}
                                                style={{
                                                    width: '100%',
                                                    padding: '14px 20px',
                                                    borderRadius: '14px',
                                                    border: `2px dashed ${BRAND.primary}`,
                                                    background: 'rgba(97, 37, 123, 0.05)',
                                                    color: BRAND.primary,
                                                    fontWeight: '800',
                                                    fontSize: '0.92rem',
                                                    cursor: canProceedToNext() ? 'pointer' : 'not-allowed',
                                                    opacity: canProceedToNext() ? 1 : 0.6,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '10px',
                                                    transition: 'all 0.2s ease',
                                                }}
                                                onMouseEnter={e => {
                                                    if (canProceedToNext()) {
                                                        e.currentTarget.style.background = 'rgba(97, 37, 123, 0.1)';
                                                        e.currentTarget.style.borderColor = BRAND.secondary;
                                                    }
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.background = 'rgba(97, 37, 123, 0.05)';
                                                    e.currentTarget.style.borderColor = BRAND.primary;
                                                }}
                                            >
                                                <PlusCircle size={20} color={BRAND.primary} />
                                                <span>+ ¿Deseas hacer otra cotización? (Agregar otro servicio)</span>
                                                {cotizacionesPendientes.length > 0 && (
                                                    <span style={{
                                                        marginLeft: 'auto',
                                                        fontSize: '0.76rem',
                                                        background: BRAND.primary,
                                                        color: '#fff',
                                                        borderRadius: '10px',
                                                        padding: '3px 10px',
                                                        fontWeight: '800'
                                                    }}>
                                                        {cotizacionesPendientes.length} guardada{cotizacionesPendientes.length > 1 ? 's' : ''}
                                                    </span>
                                                )}
                                            </button>
                                            <div style={{ fontSize: '0.74rem', color: BRAND.textMuted, textAlign: 'center' }}>
                                                Al presionar este botón se guarda este servicio y vuelves al Paso 1 para elegir otro.
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* ═════════ PASO 4: DATOS DE CONTACTO (RÁPIDO, SIN LOGIN) ═════════ */}
                            {currentStep === 3 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                                    <div style={{
                                        padding: '14px 18px',
                                        borderRadius: '14px',
                                        background: '#ecfdf5',
                                        border: '1.5px solid #d1fae5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                    }}>
                                        <ShieldCheck size={22} color="#059669" />
                                        <span style={{ fontSize: '0.84rem', color: '#065f46', fontWeight: '600' }}>
                                            No necesitas contraseña ni cuenta previa. Tus datos solo se utilizarán para enviarte el comprobante oficial y coordinar tu reserva.
                                        </span>
                                    </div>

                                    {cotizacionesPendientes.length > 0 && (
                                        <div style={{
                                            padding: '12px 18px',
                                            borderRadius: '14px',
                                            background: 'rgba(97, 37, 123, 0.08)',
                                            border: `1.5px solid ${BRAND.primary}35`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                        }}>
                                            <Building2 size={22} color={BRAND.primary} />
                                            <div>
                                                <div style={{ fontSize: '0.86rem', color: BRAND.primary, fontWeight: '800' }}>
                                                    Cotizando {cotizacionesPendientes.length + 1} servicios combinados
                                                </div>
                                                <div style={{ fontSize: '0.74rem', color: BRAND.textMuted }}>
                                                    Todos los servicios se agruparán bajo estos mismos datos de contacto.
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
                                        <div>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '800', color: BRAND.textDark, marginBottom: '6px' }}>
                                                <User size={14} color={BRAND.primary} /> Nombre(s) *
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Ej. Andrés"
                                                value={nombre}
                                                onChange={e => setNombre(e.target.value)}
                                                className="cotiz-input"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '800', color: BRAND.textDark, marginBottom: '6px' }}>
                                                <User size={14} color={BRAND.primary} /> Apellidos *
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Ej. Quispe"
                                                value={apellido}
                                                onChange={e => setApellido(e.target.value)}
                                                className="cotiz-input"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
                                        <div>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '800', color: BRAND.textDark, marginBottom: '6px' }}>
                                                <Mail size={14} color={BRAND.secondary} /> Correo Electrónico *
                                            </label>
                                            <input
                                                type="email"
                                                placeholder="tu@correo.com"
                                                value={email}
                                                onChange={e => setEmail(e.target.value)}
                                                className="cotiz-input"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '800', color: BRAND.textDark, marginBottom: '6px' }}>
                                                <Phone size={14} color={BRAND.accentDark} /> WhatsApp / Teléfono *
                                            </label>
                                            <input
                                                type="tel"
                                                placeholder="Ej. 70012345"
                                                value={telefono}
                                                onChange={e => setTelefono(e.target.value)}
                                                className="cotiz-input"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: BRAND.textDark, marginBottom: '6px' }}>
                                                Empresa / Razón Social (Opcional)
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Nombre de tu empresa o startup"
                                                value={empresa}
                                                onChange={e => setEmpresa(e.target.value)}
                                                className="cotiz-input"
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: BRAND.textDark, marginBottom: '6px' }}>
                                                CI o NIT (Opcional)
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Para factura o proforma"
                                                value={ciNit}
                                                onChange={e => setCiNit(e.target.value)}
                                                className="cotiz-input"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: BRAND.textDark, marginBottom: '6px' }}>
                                            Requerimientos Especiales o Comentarios (Opcional)
                                        </label>
                                        <textarea
                                            rows={2}
                                            placeholder="Ej. Necesitamos proyector HDMI, pizarra acrílica o servicio de café..."
                                            value={observaciones}
                                            onChange={e => setObservaciones(e.target.value)}
                                            className="cotiz-input"
                                            style={{ resize: 'vertical' }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* ═════════ PASO 5: RESUMEN Y GENERAR COTIZACIÓN ═════════ */}
                            {currentStep === 4 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: '10px',
                                    }}>
                                        <div>
                                            <h3 style={{
                                                fontSize: '1.2rem',
                                                fontWeight: '900',
                                                color: BRAND.textDark,
                                                margin: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                            }}>
                                                <FileText size={22} color={BRAND.primary} /> Resumen de Cotizaciones
                                            </h3>
                                            <span style={{ fontSize: '0.8rem', color: BRAND.textMuted }}>
                                                Revisa tus servicios cotizados antes de generar tu cotización oficial
                                            </span>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={handleAgregarOtraCotizacion}
                                            style={{
                                                padding: '8px 16px',
                                                borderRadius: '10px',
                                                border: `1.5px dashed ${BRAND.primary}`,
                                                background: 'rgba(97, 37, 123, 0.05)',
                                                color: BRAND.primary,
                                                fontWeight: '800',
                                                fontSize: '0.82rem',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                            }}
                                        >
                                            <PlusCircle size={15} />
                                            <span>+ Agregar otro servicio</span>
                                        </button>
                                    </div>

                                    {listaCotizacionesResumen.length === 0 ? (
                                        <div style={{
                                            padding: '36px 20px',
                                            borderRadius: '16px',
                                            background: '#f8fafc',
                                            border: '1.5px dashed #cbd5e1',
                                            textAlign: 'center',
                                        }}>
                                            <p style={{ color: BRAND.textMuted, marginBottom: '14px', fontWeight: '600' }}>
                                                No tienes ningún servicio seleccionado para cotizar.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => setCurrentStep(0)}
                                                style={{
                                                    padding: '10px 22px',
                                                    borderRadius: '12px',
                                                    background: BRAND.primary,
                                                    color: '#fff',
                                                    border: 'none',
                                                    fontWeight: '700',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                Elegir un Servicio
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                            {listaCotizacionesResumen.map((cot, idx) => (
                                                <div
                                                    key={cot.id || idx}
                                                    style={{
                                                        background: '#ffffff',
                                                        borderRadius: '16px',
                                                        border: `1.5px solid ${BRAND.primary}25`,
                                                        padding: '18px 20px',
                                                        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                                                    }}
                                                >
                                                    {/* Card Header */}
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        borderBottom: '1px solid #f1f5f9',
                                                        paddingBottom: '12px',
                                                        marginBottom: '14px',
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                            <span style={{
                                                                background: BRAND.primary,
                                                                color: '#ffffff',
                                                                fontSize: '0.74rem',
                                                                fontWeight: '800',
                                                                padding: '3px 10px',
                                                                borderRadius: '8px',
                                                            }}>
                                                                Cotización #{idx + 1}
                                                            </span>
                                                            <span style={{
                                                                background: '#f1f5f9',
                                                                color: BRAND.textDark,
                                                                fontSize: '0.76rem',
                                                                fontWeight: '700',
                                                                padding: '3px 10px',
                                                                borderRadius: '8px',
                                                            }}>
                                                                📍 {cot.sucursalNombre || 'Sucursal'}
                                                            </span>
                                                        </div>

                                                        {/* Botón Eliminar Cotización */}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleEliminarCotizacionPendiente(cot.id)}
                                                            title="Eliminar esta cotización de la lista"
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '5px',
                                                                background: '#fee2e2',
                                                                color: '#dc2626',
                                                                border: '1px solid #fecaca',
                                                                borderRadius: '8px',
                                                                padding: '5px 10px',
                                                                fontSize: '0.74rem',
                                                                fontWeight: '800',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s ease',
                                                            }}
                                                            onMouseEnter={e => {
                                                                e.currentTarget.style.background = '#dc2626';
                                                                e.currentTarget.style.color = '#ffffff';
                                                            }}
                                                            onMouseLeave={e => {
                                                                e.currentTarget.style.background = '#fee2e2';
                                                                e.currentTarget.style.color = '#dc2626';
                                                            }}
                                                        >
                                                            <Trash2 size={13} />
                                                            <span>Eliminar</span>
                                                        </button>
                                                    </div>

                                                    {/* Card Body */}
                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                                        gap: '12px',
                                                        marginBottom: '14px',
                                                    }}>
                                                        <div>
                                                            <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: BRAND.textMuted, fontWeight: '700' }}>Servicio</span>
                                                            <div style={{ fontSize: '0.92rem', fontWeight: '800', color: BRAND.primary }}>
                                                                {cot.servicioNombre}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: BRAND.textMuted, fontWeight: '700' }}>Espacio</span>
                                                            <div style={{ fontSize: '0.92rem', fontWeight: '800', color: BRAND.textDark }}>
                                                                {cot.espacioNombre || 'Sin espacio físico'}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: BRAND.textMuted, fontWeight: '700' }}>Modalidad / Plan</span>
                                                            <div style={{ fontSize: '0.92rem', fontWeight: '800', color: BRAND.secondary }}>
                                                                {cot.planNombre} ({cot.cantidadPeriodos} {cot.unidad === 'dia' ? 'día(s)' : 'hora(s)'})
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: BRAND.textMuted, fontWeight: '700' }}>Fecha y Horario</span>
                                                            <div style={{ fontSize: '0.92rem', fontWeight: '800', color: BRAND.textDark }}>
                                                                {fechaCotizacion(cot.fechaInicio)} — {fechaCotizacion(cot.fechaFin)} • {cot.horaInicio} a {cot.horaFin}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: BRAND.textMuted, fontWeight: '700' }}>Asistentes</span>
                                                            <div style={{ fontSize: '0.92rem', fontWeight: '800', color: BRAND.textDark }}>
                                                                {cot.personas} {cot.personas === 1 ? 'persona' : 'personas'}
                                                            </div>
                                                        </div>
                                                        {cot.observaciones && (
                                                            <div>
                                                                <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: BRAND.textMuted, fontWeight: '700' }}>Notas</span>
                                                                <div style={{ fontSize: '0.82rem', color: BRAND.textDark, fontStyle: 'italic' }}>
                                                                    {cot.observaciones}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Card Footer: Individual Total */}
                                                    <div style={{
                                                        borderTop: '1px dashed #e2e8f0',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        background: '#faf5fc',
                                                        margin: '0 -20px -18px -20px',
                                                        padding: '10px 20px',
                                                        borderBottomLeftRadius: '14px',
                                                        borderBottomRightRadius: '14px',
                                                    }}>
                                                        <span style={{ fontSize: '0.82rem', fontWeight: '700', color: BRAND.textMuted }}>
                                                            Pago Total Individual:
                                                        </span>
                                                        <span style={{ fontSize: '1.2rem', fontWeight: '900', color: BRAND.primary }}>
                                                            Bs. {cot.precioEstimado.toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Cupón & Cliente summary */}
                                    {listaCotizacionesResumen.length > 0 && (
                                        <>
                                            <div style={{
                                                padding: '14px 18px',
                                                borderRadius: '14px',
                                                background: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                flexWrap: 'wrap',
                                                gap: '10px',
                                            }}>
                                                <div>
                                                    <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: BRAND.textMuted, fontWeight: '700' }}>Titular de la cotización</span>
                                                    <div style={{ fontSize: '0.92rem', fontWeight: '800', color: BRAND.textDark }}>
                                                        {nombre} {apellido} • 📞 {telefono} • ✉️ {email}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Cupón */}
                                            <div style={{
                                                padding: '14px',
                                                borderRadius: '12px',
                                                background: '#fff',
                                                border: '1px solid #e2e8f0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                            }}>
                                                <Tag size={16} color={BRAND.accentDark} />
                                                <input
                                                    type="text"
                                                    placeholder="Código de cupón promocional (opcional)"
                                                    value={cuponCodigo}
                                                    onChange={e => setCuponCodigo(e.target.value.toUpperCase())}
                                                    style={{
                                                        flex: 1,
                                                        border: 'none',
                                                        outline: 'none',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '700',
                                                        color: BRAND.textDark,
                                                    }}
                                                />
                                            </div>

                                            {/* Cuadro General de Totales */}
                                            <div style={{
                                                background: `linear-gradient(135deg, ${BRAND.primary}12, ${BRAND.secondary}15)`,
                                                borderRadius: '18px',
                                                border: `2px solid ${BRAND.primary}35`,
                                                padding: '20px 24px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: '14px',
                                            }}>
                                                <div>
                                                    <span style={{ fontSize: '0.88rem', color: BRAND.primary, fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                        Pago Total General ({listaCotizacionesResumen.length} servicio{listaCotizacionesResumen.length > 1 ? 's' : ''})
                                                    </span>
                                                    <div style={{ fontSize: '0.76rem', color: BRAND.textMuted, marginTop: '2px' }}>
                                                        Suma total de todas las cotizaciones seleccionadas
                                                    </div>
                                                </div>
                                                <div style={{
                                                    fontSize: '2.2rem',
                                                    fontWeight: '900',
                                                    color: BRAND.primary,
                                                    letterSpacing: '-0.03em',
                                                }}>
                                                    Bs. {grandTotalEstimado.toFixed(2)}
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {submitError && (
                                        <div style={{
                                            padding: '14px 18px',
                                            borderRadius: '12px',
                                            background: '#fef2f2',
                                            border: '1.5px solid #fecaca',
                                            color: '#b91c1c',
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}>
                                            <AlertCircle size={18} />
                                            {submitError}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ═════════ PANTALLA 5: ÉXITO, RESUMEN MULTI-COTIZACIÓN Y BOTONES SEPARADOS ═════════ */}
                            {currentStep === 5 && cotizacionesFinales.length > 0 && (() => {
                                const totalConfirmado = cotizacionesFinales.reduce((s, c) => s + c.resultado.total, 0);
                                return (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '16px 8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '20px',
                                    }}>
                                        <div style={{
                                            width: '72px',
                                            height: '72px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #10b981, #059669)',
                                            color: '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 12px 32px rgba(16, 185, 129, 0.35)',
                                        }}>
                                            <CheckCircle2 size={44} />
                                        </div>

                                        <div>
                                            <span style={{
                                                fontSize: '0.78rem',
                                                fontWeight: '800',
                                                color: BRAND.secondary,
                                                letterSpacing: '0.14em',
                                                textTransform: 'uppercase',
                                            }}>
                                                Cotización Guardada Exitosamente
                                            </span>
                                            <h3 style={{
                                                fontSize: '1.6rem',
                                                fontWeight: '900',
                                                color: BRAND.textDark,
                                                margin: '6px 0 8px',
                                            }}>
                                                ¡Tu{cotizacionesFinales.length > 1 ? 's Cotizaciones están Listas' : ' Cotización está Lista'}!
                                            </h3>
                                            <p style={{
                                                fontSize: '0.92rem',
                                                color: BRAND.textMuted,
                                                maxWidth: '520px',
                                                lineHeight: 1.5,
                                                margin: '0 auto',
                                            }}>
                                                Guardamos tus cotizaciones y solicitudes con sus códigos. Descarga el PDF o envía el código a la sucursal para oficializar la reserva desde POS.
                                            </p>
                                        </div>

                                        {/* Tarjetas individuales de cada cotización generada */}
                                        <div style={{
                                            width: '100%',
                                            maxWidth: '540px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '12px',
                                        }}>
                                            {cotizacionesFinales.map((item) => (
                                                <div
                                                    key={item.id}
                                                    style={{
                                                        background: '#ffffff',
                                                        borderRadius: '14px',
                                                        border: '1px solid #e2e8f0',
                                                        padding: '16px',
                                                        textAlign: 'left',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '8px',
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        borderBottom: '1px solid #f1f5f9',
                                                        paddingBottom: '8px',
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span style={{
                                                                fontSize: '0.72rem',
                                                                fontWeight: '800',
                                                                background: `${BRAND.primary}15`,
                                                                color: BRAND.primary,
                                                                padding: '3px 8px',
                                                                borderRadius: '6px',
                                                            }}>
                                                                #{item.resultado.codigo}
                                                            </span>
                                                            <strong style={{ fontSize: '0.9rem', color: BRAND.textDark }}>
                                                                {item.resultado.servicio}
                                                            </strong>
                                                        </div>

                                                        {/* Eliminar de la lista si ya no la desea */}
                                                        {cotizacionesFinales.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleEliminarCotizacion(item.id)}
                                                                title="Quitar esta cotización del comprobante"
                                                                style={{
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    color: '#94a3b8',
                                                                    cursor: 'pointer',
                                                                    padding: '4px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    transition: 'color 0.2s ease',
                                                                }}
                                                                onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                                                                onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '0.82rem' }}>
                                                        <div>
                                                            <span style={{ color: BRAND.textMuted }}>Espacio: </span>
                                                            <strong style={{ color: BRAND.textDark }}>{item.resultado.espacio || 'Sin espacio'}</strong>
                                                        </div>
                                                        <div>
                                                            <span style={{ color: BRAND.textMuted }}>Fecha: </span>
                                                            <strong style={{ color: BRAND.textDark }}>{fechaCotizacion(item.resultado.fecha_inicio)} — {fechaCotizacion(item.resultado.fecha_fin)}</strong>
                                                        </div>
                                                        <div>
                                                            <span style={{ color: BRAND.textMuted }}>Horario: </span>
                                                            <strong style={{ color: BRAND.textDark }}>{item.resultado.hora_inicio || item.pendiente.horaInicio} - {item.resultado.hora_fin || item.pendiente.horaFin}</strong>
                                                        </div>
                                                        <div>
                                                            <span style={{ color: BRAND.textMuted }}>Total Individual: </span>
                                                            <strong style={{ color: BRAND.primary, fontSize: '0.92rem' }}>Bs. {item.resultado.total.toFixed(2)}</strong>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Cuadro Total General */}
                                            <div style={{
                                                background: '#f8fafc',
                                                borderRadius: '14px',
                                                padding: '14px 20px',
                                                border: '1.5px solid #e2e8f0',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}>
                                                <div>
                                                    <span style={{ fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: '800', color: BRAND.textMuted }}>
                                                        Pago Total General ({cotizacionesFinales.length} servicio{cotizacionesFinales.length > 1 ? 's' : ''}):
                                                    </span>
                                                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                                        Monto total combinado
                                                    </div>
                                                </div>
                                                <div style={{ fontSize: '1.5rem', fontWeight: '900', color: BRAND.primary }}>
                                                    Bs. {totalConfirmado.toFixed(2)}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action Buttons: Separados */}
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '12px',
                                            alignItems: 'center',
                                            width: '100%',
                                            maxWidth: '540px',
                                            marginTop: '6px',
                                        }}>
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                                                gap: '12px',
                                                width: '100%',
                                            }}>
                                                {/* Botón 1: Descargar PDF */}
                                                <button
                                                    type="button"
                                                    onClick={handleDescargarPDF}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '10px',
                                                        padding: '15px 22px',
                                                        borderRadius: '14px',
                                                        border: 'none',
                                                        background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.secondary})`,
                                                        color: '#ffffff',
                                                        fontWeight: '800',
                                                        fontSize: '0.94rem',
                                                        cursor: 'pointer',
                                                        boxShadow: '0 8px 24px rgba(97,37,123,0.3)',
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 12px 28px rgba(97,37,123,0.4)';
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(97,37,123,0.3)';
                                                    }}
                                                >
                                                    <Download size={19} />
                                                    <span>Descargar Cotización (PDF)</span>
                                                </button>

                                                {/* Botón 2: WhatsApp */}
                                                {Array.from(new Set(cotizacionesFinales.map(item => item.resultado.sucursal))).map(sucursal => <button
                                                    key={sucursal}
                                                    disabled={!cotizacionesFinales.find(item => item.resultado.sucursal === sucursal)?.resultado.whatsapp_url}
                                                    type="button"
                                                    onClick={() => handleEnviarWhatsapp(sucursal)}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '10px',
                                                        padding: '15px 22px',
                                                        borderRadius: '14px',
                                                        border: 'none',
                                                        background: `linear-gradient(135deg, #25D366, #128C7E)`,
                                                        color: '#ffffff',
                                                        fontWeight: '800',
                                                        fontSize: '0.94rem',
                                                        cursor: 'pointer',
                                                        boxShadow: '0 8px 24px rgba(37, 211, 102, 0.35)',
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 12px 28px rgba(37, 211, 102, 0.45)';
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(37, 211, 102, 0.35)';
                                                    }}
                                                >
                                                    <MessageCircle size={19} />
                                                    <span>WhatsApp · {sucursal}</span>
                                                </button>)}
                                            </div>

                                            <button
                                                type="button"
                                                onClick={iniciarNuevaCotizacion}
                                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                                    padding: '12px 22px', borderRadius: '12px', border: `1.5px solid ${BRAND.primary}`,
                                                    background: '#fff', color: BRAND.primary, fontWeight: '800', cursor: 'pointer' }}
                                            >
                                                <PlusCircle size={18} /> Nueva cotización
                                            </button>

                                            <p style={{ fontSize: '0.76rem', color: BRAND.textMuted, margin: 0, textAlign: 'center' }}>
                                                Tu cotización está guardada como solicitud. Comparte el código por WhatsApp para que nuestro equipo oficialice la reserva desde POS.
                                            </p>
                                        </div>
                                    </div>
                                );
                            })()}
                        </>
                    )}
                </div>

                {/* ═══ MODAL FOOTER CONTROLS ═══ */}
                {currentStep < 5 && (
                    <div style={{
                        padding: '16px 28px',
                        borderTop: '1px solid #f1f5f9',
                        background: '#fafafc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                        {currentStep > 0 ? (
                            <button
                                type="button"
                                onClick={handleBack}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '10px 20px',
                                    borderRadius: '12px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#ffffff',
                                    color: '#475569',
                                    fontWeight: '700',
                                    fontSize: '0.88rem',
                                    cursor: 'pointer',
                                }}
                            >
                                <ChevronLeft size={16} /> Volver
                            </button>
                        ) : (
                            <div />
                        )}

                        {currentStep < 4 ? (
                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={!canProceedToNext()}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '12px 28px',
                                    borderRadius: '14px',
                                    border: 'none',
                                    background: canProceedToNext()
                                        ? `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.secondary})`
                                        : '#cbd5e1',
                                    color: '#ffffff',
                                    fontWeight: '800',
                                    fontSize: '0.92rem',
                                    cursor: canProceedToNext() ? 'pointer' : 'not-allowed',
                                    boxShadow: canProceedToNext() ? '0 6px 20px rgba(97,37,123,0.3)' : 'none',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <span>Siguiente</span>
                                <ChevronRight size={16} />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleGenerarCotizacion}
                                disabled={submitting || listaCotizacionesResumen.length === 0}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '14px 34px',
                                    borderRadius: '14px',
                                    border: 'none',
                                    background: (submitting || listaCotizacionesResumen.length === 0)
                                        ? '#cbd5e1'
                                        : `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.secondary})`,
                                    color: '#ffffff',
                                    fontWeight: '900',
                                    fontSize: '1rem',
                                    cursor: (submitting || listaCotizacionesResumen.length === 0) ? 'not-allowed' : 'pointer',
                                    boxShadow: (submitting || listaCotizacionesResumen.length === 0) ? 'none' : '0 8px 28px rgba(97,37,123,0.38)',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {submitting ? (
                                    <>
                                        <RefreshCw size={18} className="animate-spin" />
                                        <span>Generando Cotizaciones...</span>
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={18} />
                                        <span>{listaCotizacionesResumen.length > 1 ? 'Generar Cotizaciones' : 'Generar Cotización'}</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CotizacionModal;



